import React, { useContext, useState } from "react";
import { Visibility as PreviewIcon } from "@mui/icons-material";
import { IconButton, Modal, TableRow } from "@mui/material";
import { MaterialImport, MaterialOrder } from "../../../../data/entities";
import { color } from "../../../../shared/styles";
import { StyledTableCell } from "../../../../shared/components/CustomTable/Styled";
import TextBox from "../../../../shared/components/TextBox";
import DeleteIcon from "../../../../shared/svg/DeleteIcon";
import {
    ButtonRow,
    Expandable,
    InfoText,
    ModalWrapper,
    StyledBackdrop,
    SummaryTableCell,
    SummaryTableRow,
    TitleText,
    TitleWrapper,
} from "../../single/AddMaterialModal/Styled";
import apiMethods from "../../../../utils/api";
import { error, success } from "../../../../utils/toast";
import { TableWarningMessage } from "../../../ProductOrder/single/AddProductModal/Styled";
import WarningTableIcon from "../../../../shared/svg/WarningTableIcon";
import CustomTable from "../../../../shared/components/CustomTable";
import { StyledButton } from "../../Styled";
import { ImportMissingMaterialTableColumn } from "../../../../configs/productOrder";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import Calendar from "../../../../shared/svg/Calendar";
import { formatNumber, formatInternationalNumber, parseInternationalToNumber } from "../../../../utils/format";
import Template from "../../../../templates/PMS - Phiếu nhập kho vật tư.docx";
import { generateDocumentBlob } from "../../../../utils/exportUtils";
import { buildDocxData } from "../../../../utils/docxBuilder";
import { materialImportPreviewDescriptor } from "../../../../utils/docxMappings";
import DocxPreviewModal from "../../../../shared/components/DocxPreviewModal";
import LoadingButton from "../../../../shared/components/LoadingButton";
import { AuthenticationContext } from "../../../../provider/AuthenticationProvider";

const getSelectedQuantity = (material: any) => {
    return material.selectedQuantity !== undefined ? material.selectedQuantity : 0;
};

const getMissingQuantity = (material: any) => {
    return material.missingQuantity !== undefined
        ? material.missingQuantity
        : getSelectedQuantity(material) - material.quantity;
};

const getImportedQuantity = (material: any) => {
    return material.importedQuantity !== undefined ? material.importedQuantity : getMissingQuantity(material);
};

const isQuantitySufficient = (materialImport: any) => {
    return getImportedQuantity(materialImport) <= materialImport.missingQuantity;
};

const processMaterialImports = (materialImports: MaterialImport[]) => {
    return materialImports.map((item) => {
        return {
            id: item.material.id,
            name: item.material.name,
            missingQuantity: item.missing_quantity,
            importedQuantity: item.quantity,
        };
    });
};

interface MaterialRowProps {
    item: any;
    extraRowProps: any;
}

const MaterialRow = ({ item, extraRowProps }: MaterialRowProps) => {
    const isImportedQuantityMatched = getMissingQuantity(item) === getImportedQuantity(item);
    const backgroundColor = isImportedQuantityMatched ? "" : color.status.DANGER_LIGHT;
    const selectedQuantityBorderColor = isImportedQuantityMatched ? "" : color.status.FAIL;

    const materialCode = item.material_code === null ? item.id : item.material_code;

    const providerName = (() => {
        try {
            const materialOrder = extraRowProps?.materialOrder as MaterialOrder | undefined;
            const matched = materialOrder?.materials?.find((m: any) => {
                const materialId = m?.material ? m?.material?.id : m?.id;
                return materialId === item?.id;
            });
            return (matched as any)?.provider_name || "";
        } catch {
            return "";
        }
    })();

    return !item.isSummary ? (
        <TableRow sx={{ backgroundColor: `${backgroundColor} !important` }}>
            <StyledTableCell align="left">{materialCode}</StyledTableCell>
            <StyledTableCell align="left">{item.name}</StyledTableCell>
            <StyledTableCell align="left">{providerName}</StyledTableCell>
            <StyledTableCell align="left">{formatNumber(getMissingQuantity(item))}</StyledTableCell>
            <StyledTableCell align="left">
                <TextBox
                    numeric
                    style={{ borderColor: selectedQuantityBorderColor, backgroundColor }}
                    text={formatInternationalNumber(getImportedQuantity(item) ?? "")}
                    onChangeText={(value: string) => {
                        const n = parseInternationalToNumber(value, error);
                        if (!Number.isFinite(n)) return;
                        extraRowProps.onChangeMaterialImportedQuantity(item.id, n);
                    }}
                />
            </StyledTableCell>
            <StyledTableCell align="left">
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="vi">
                    <DemoContainer
                        components={["DatePicker"]}
                        sx={{
                            paddingTop: "0px",
                            width: "100%",
                            "& .MuiTextField-root": {
                                width: "100%",
                            },
                        }}
                    >
                        <DatePicker
                            value={item.due_date ? dayjs(item.due_date) : null}
                            onChange={(newValue) => {
                                extraRowProps.onChangeDueDate(
                                    item.id,
                                    newValue ? newValue.format("YYYY-MM-DD") : null,
                                );
                            }}
                            format="DD/MM/YYYY"
                            slotProps={{
                                textField: {
                                    placeholder: "DD/MM/YYYY",
                                },
                                popper: {
                                    placement: "bottom-end",
                                    sx: {
                                        "& .MuiPaper-root": {
                                            marginTop: "4px",
                                        },
                                        "& .MuiPickersDay-root.Mui-selected": {
                                            backgroundColor: "#0058A0",
                                            "&:hover": {
                                                backgroundColor: "#0058A0",
                                            },
                                        },
                                        "& .MuiDayCalendar-weekDayLabel": {
                                            color: "#333739",
                                        },
                                    },
                                },
                            }}
                            dayOfWeekFormatter={(day) => {
                                const weekdays = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];
                                return weekdays[day.day()];
                            }}
                            sx={{
                                width: "100%",
                                "& .MuiInputBase-root": {
                                    height: "40px",
                                    fontSize: "14px",
                                    fontStyle: "normal",
                                    fontWeight: 400,
                                    lineHeight: "normal",
                                    fontFamily: "Noto Sans",
                                    padding: "8px 12px",
                                },
                                "& .MuiInputBase-input": {
                                    padding: "0px",
                                    height: "22px",
                                    "&::placeholder": {
                                        color: "#757575",
                                        opacity: 1,
                                    },
                                },
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        border: `1px solid ${color.emphasis.NEUTRAL_N90}`,
                                        borderRadius: "5px",
                                    },
                                    "&:hover fieldset": {
                                        border: `1px solid ${color.emphasis.NEUTRAL_N90}`,
                                    },
                                    "&.Mui-focused fieldset": {
                                        border: `1px solid ${color.emphasis.NEUTRAL_N90}`,
                                    },
                                },
                            }}
                            slots={{
                                openPickerIcon: Calendar,
                            }}
                        />
                    </DemoContainer>
                </LocalizationProvider>
            </StyledTableCell>
            <StyledTableCell align="left">
                <IconButton onClick={() => extraRowProps.removeSelectedItem(item)}>
                    <DeleteIcon />
                </IconButton>
            </StyledTableCell>
        </TableRow>
    ) : (
        <SummaryTableRow>
            <SummaryTableCell align="left">{item.id}</SummaryTableCell>
            <SummaryTableCell align="left">{item.name}</SummaryTableCell>
            <SummaryTableCell align="left"></SummaryTableCell>
            <SummaryTableCell align="left"></SummaryTableCell>
            <SummaryTableCell align="left"></SummaryTableCell>
            <SummaryTableCell align="left"></SummaryTableCell>
        </SummaryTableRow>
    );
};

interface AddImportMaterialModalProps {
    open: boolean;
    handleClose: any;
    materialOrder: MaterialOrder;
    mergeMaterialOrder: Function;
    fetchMaterialImports: Function;
    fetchMaterialOrder: Function;
}

export const AddImportMaterialModal = ({
                                           open,
                                           handleClose,
                                           materialOrder,
                                           fetchMaterialImports,
                                           fetchMaterialOrder,
                                       }: AddImportMaterialModalProps) => {
    const { user } = useContext(AuthenticationContext);
    const [isOpen, setIsOpen] = useState(open);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { materials } = materialOrder;

    const [tableItems, setTableItems] = useState(() =>
        materials.filter((item) => getMissingQuantity(item) !== 0),
    );

    let isAllQuantityMatched = true;
    tableItems.forEach((item: any) => {
        if (item.id !== "Subtotal" && getMissingQuantity(item) !== getImportedQuantity(item)) {
            isAllQuantityMatched = false;
        }
    });

    const handleCloseWithAnimation = () => {
        if (isSubmitting) return;
        setIsOpen(false);
        setTimeout(handleClose, 200);
    };

    const onChangeMaterialImportedQuantity = (id: string, quantity: any) => {
        const updatedMaterials = tableItems.map((material: any) => {
            if (material.id === id) return { ...material, importedQuantity: quantity };
            return material;
        });
        setTableItems(updatedMaterials);
    };

    const onChangeDueDate = (id: string, date: string) => {
        const updatedMaterials = tableItems.map((material: any) => {
            if (material.id === id) return { ...material, due_date: date };
            return material;
        });
        setTableItems(updatedMaterials);
    };

    const handlePreview = async () => {
        try {
            const formData = {
                provider_name: "",
                deliverer: "",
                input_method: "",
                note: "",
                import_date: dayjs().format("YYYY-MM-DD"),
            };

            const selectedMaterials = (tableItems as any[])
                .filter((m: any) => m.id !== "Subtotal")
                .map((m: any) => ({
                    id: m.id,
                    name: m.name,
                    measurement_unit: m.measurement_unit,
                    material_code: m.material_code || m.id,
                    importQuantity: getImportedQuantity(m),
                    missingQuantity: getMissingQuantity(m),
                    storage: m.storage,
                }));

            const stateForDoc = { formData, selectedMaterials };
            const data = buildDocxData(stateForDoc, materialImportPreviewDescriptor);
            const blob = await generateDocumentBlob(data, Template);
            setPreviewBlob(blob);
            setIsPreviewOpen(true);
        } catch (e) {
            console.error("Preview generation failed", e);
        }
    };

    const onClickImport = async () => {
        if (isSubmitting) return;

        const processedData: any[] = [];

        tableItems.forEach((material: any) => {
            if (material.id === `Subtotal`) return;

            const materialImportData = {
                material_id: material.id,
                quantity: getImportedQuantity(material),
                missing_quantity: getMissingQuantity(material),
                storage_keeper_id: user?.id,
                due_date: material.due_date === undefined ? null : material.due_date, // If not pass due_date, it will be null (unlimited time)
            };
            processedData.push(materialImportData);
        });

        try {
            setIsSubmitting(true);
            await apiMethods.post(`/materials/orders/${materialOrder?.id}/imports/`, processedData);
            success("Nhập kho thành công", `Nhập kho thành công`);
            await fetchMaterialImports();
            await fetchMaterialOrder();
            handleCloseWithAnimation();
        } catch (err) {
            error(`Nhập kho thất bại`, err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const removeSelectedItem = (removedItem: any) => {
        const removedSelectedItems: any[] = (tableItems as any[]).filter((item: any) => item.name !== removedItem.name);
        setTableItems(removedSelectedItems);
    };

    return (
        <>
            <Modal
                open={open}
                onClose={handleCloseWithAnimation}
                closeAfterTransition
                BackdropComponent={StyledBackdrop}
                BackdropProps={{
                    timeout: 500,
                    onClick: (e) => e.stopPropagation(),
                }}
            >
                <ModalWrapper isOpen={isOpen}>
                    <Expandable>
                        <TitleWrapper>
                            <TitleText>Nhập kho vật tư</TitleText>

                            <InfoText>Vui lòng thêm số lượng nhập cho từng vật tư bị thiếu sau:</InfoText>
                        </TitleWrapper>

                        {!isAllQuantityMatched && (
                            <TableWarningMessage>
                                <WarningTableIcon />

                                <span
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        whiteSpace: "pre-wrap",
                                    }}
                                >
                                <span style={{ fontWeight: "700" }}>Lưu ý quan trọng: </span>
                                Số lượng nhập kho chênh lệch với số lượng yêu cầu.
                            </span>
                            </TableWarningMessage>
                        )}

                        <CustomTable
                            itemList={tableItems}
                            RowElement={MaterialRow}
                            TableColumns={ImportMissingMaterialTableColumn}
                            extraRowProps={{
                                materialOrder,
                                onChangeMaterialImportedQuantity,
                                onChangeDueDate,
                                removeSelectedItem,
                            }}
                            containerStyle={{
                                height: "350px",
                                overflow: "auto",
                                marginBottom: "8px",
                            }}
                        />
                    </Expandable>

                    <ButtonRow>
                        <StyledButton variant="outlined" onClick={handleCloseWithAnimation}>Huỷ bỏ</StyledButton>
                        <StyledButton onClick={handlePreview} startIcon={<PreviewIcon />} variant="contained">
                            Xem trước phiếu
                        </StyledButton>
                    </ButtonRow>
                </ModalWrapper>
            </Modal>
            <DocxPreviewModal
                open={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                blob={previewBlob}
                title="Xem trước phiếu nhập kho vật tư"
                BackdropComponent={StyledBackdrop}
                backdropProps={{ timeout: 300 }}
                debug
                actions={
                    <LoadingButton
                        variant="contained"
                        onClick={onClickImport}
                        text="Nhập kho"
                        disabled={isSubmitting}
                        isLoading={isSubmitting}
                        style={undefined}
                    />
                }
            />
        </>
    );
};
